import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { MatFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CONSULTA_PANTALLA_ANGOSTA_SUPER_EJECUTIVO, NOMBRE_SUPER_EJECUTIVO } from '../super-ejecutivo.constants';
import { IAreaPanelSuperEjecutivo, TamanoPanelSuperEjecutivo } from '../super-ejecutivo.interface';
import { SuperEjecutivoService } from '../super-ejecutivo.service';
import { SuperEjecutivoPanelComponent } from '../super-ejecutivo-panel/super-ejecutivo-panel.component';

/**
 * Botón flotante del Super Ejecutivo, abajo a la derecha en todas las pantallas con sesión (RN2, RN3), y su panel.
 * Solo se monta para super usuarios (RN1, en el layout).
 */
@Component({
  selector: 'app-super-ejecutivo-flotante',
  templateUrl: './super-ejecutivo-flotante.component.html',
  styleUrl: './super-ejecutivo-flotante.component.css',
  imports: [MatFabButton, MatIcon, MatTooltip, SuperEjecutivoPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperEjecutivoFlotanteComponent {
  public readonly superEjecutivo = inject(SuperEjecutivoService);
  public readonly nombre = NOMBRE_SUPER_EJECUTIVO;
  private readonly zona = inject(NgZone);

  /** Área central donde se muestran las pantallas de los menús (RN5). */
  public readonly areaCentral = input<HTMLElement | undefined>();
  /** Barra superior: el panel agrandado empieza debajo de ella (RN5). */
  public readonly barraSuperior = input<HTMLElement | undefined>();

  /** RN5.1: en pantallas angostas el panel ocupa toda la pantalla. */
  public readonly angosta = toSignal(
    inject(BreakpointObserver)
      .observe(CONSULTA_PANTALLA_ANGOSTA_SUPER_EJECUTIVO)
      .pipe(map((estado) => estado.matches)),
    { initialValue: false },
  );

  public readonly tamanoPanel = computed<TamanoPanelSuperEjecutivo>(() => (this.angosta() ? 'completo' : this.superEjecutivo.tamano()));
  public readonly area = signal<IAreaPanelSuperEjecutivo | null>(null);

  constructor() {
    // RN8.2: recupera la conversación para saber si quedó una propuesta pendiente.
    void this.superEjecutivo.cargarConversacion();

    // RN5: el panel agrandado sigue al área central (menú abierto o cerrado, ventana más chica).
    effect((alLimpiar) => {
      const areaCentral = this.areaCentral();
      const barraSuperior = this.barraSuperior();

      if (!areaCentral) {
        return;
      }

      const medir = () => this.zona.run(() => this.area.set(this.medirArea(areaCentral, barraSuperior)));
      const observador = new ResizeObserver(medir);
      observador.observe(areaCentral);

      if (barraSuperior) {
        observador.observe(barraSuperior);
      }

      window.addEventListener('resize', medir);
      medir();
      alLimpiar(() => {
        observador.disconnect();
        window.removeEventListener('resize', medir);
      });
    });

    // Al cerrar sesión el layout se destruye: la conversación no pasa al siguiente usuario.
    inject(DestroyRef).onDestroy(() => this.superEjecutivo.reiniciar());
  }

  public alternarPanel(): void {
    if (this.superEjecutivo.abierto()) {
      this.superEjecutivo.cerrar();
    } else {
      this.superEjecutivo.abrir();
    }
  }

  private medirArea(areaCentral: HTMLElement, barraSuperior: HTMLElement | undefined): IAreaPanelSuperEjecutivo {
    const contenido = areaCentral.getBoundingClientRect();
    const top = Math.max(contenido.top, barraSuperior?.getBoundingClientRect().bottom ?? contenido.top);

    return { top, left: contenido.left, width: contenido.width, height: window.innerHeight - top };
  }
}
